import { useState, useEffect, useMemo } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Textarea,
  Alert,
  AlertIcon,
  Checkbox,
  Badge,
  Tooltip,
  Box,
  Progress,
  Icon,
  Collapse,
  useToast,
} from '@chakra-ui/react'
import {
  FiGrid,
  FiCheck,
  FiX,
  FiInfo,
  FiFolder,
  FiFile,
  FiClock,
  FiChevronRight,
  FiChevronDown,
  FiArrowRight,
} from 'react-icons/fi'
import { Folder, FolderScope, SmartOrganizePlan, SmartOrganizeFileMove } from '@/types'
import apiService from '@/services/api'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface SmartOrganizeJob {
  job_id: string
  status: string
  progress: number
  total_files: number
  processed_files: number
  created_at: string
  completed_at?: string
  error_message?: string
}

interface FolderTreeNode {
  name: string
  path: string
  description: string
  children: FolderTreeNode[]
  files: SmartOrganizeFileMove[]
}

interface SmartOrganizeModalProps {
  isOpen: boolean
  onClose: () => void
  /** null is the root of the tree — the dashboard's "All Documents" view. */
  folder: Folder | null
  /** Explorer selection frozen when the modal opened. Empty means "everything in scope". */
  selectedFileIds?: number[]
  /** Selected files left out because their text had not been extracted yet. */
  skippedCount?: number
  folderFileCount?: number
  onOrganizeComplete: () => void
}

function buildFolderTree(plan: SmartOrganizePlan): FolderTreeNode[] {
  const root: FolderTreeNode[] = []
  const nodeMap: Record<string, FolderTreeNode> = {}

  // Create nodes for all folders
  for (const folder of plan.folders) {
    const parts = folder.path.split('/')
    let currentPath = ''

    for (let i = 0; i < parts.length; i++) {
      const parentPath = currentPath
      currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i]

      if (!nodeMap[currentPath]) {
        const node: FolderTreeNode = {
          name: parts[i],
          path: currentPath,
          description: i === parts.length - 1 ? folder.description : '',
          children: [],
          files: [],
        }
        nodeMap[currentPath] = node

        if (parentPath && nodeMap[parentPath]) {
          nodeMap[parentPath].children.push(node)
        } else if (!parentPath) {
          root.push(node)
        }
      } else if (i === parts.length - 1) {
        // Update description for the leaf node
        nodeMap[currentPath].description = folder.description
      }
    }
  }

  // Assign files to their target folders
  for (const move of plan.file_moves) {
    const targetNode = nodeMap[move.target_folder]
    if (targetNode) {
      targetNode.files.push(move)
    } else {
      // Create missing folder node and add to root
      const parts = move.target_folder.split('/')
      let currentPath = ''
      for (let i = 0; i < parts.length; i++) {
        const parentPath = currentPath
        currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i]

        if (!nodeMap[currentPath]) {
          const node: FolderTreeNode = {
            name: parts[i],
            path: currentPath,
            description: '',
            children: [],
            files: [],
          }
          nodeMap[currentPath] = node

          if (parentPath && nodeMap[parentPath]) {
            nodeMap[parentPath].children.push(node)
          } else if (!parentPath) {
            root.push(node)
          }
        }
      }
      nodeMap[move.target_folder].files.push(move)
    }
  }

  return root
}

function countFilesInTree(node: FolderTreeNode): number {
  let count = node.files.length
  for (const child of node.children) {
    count += countFilesInTree(child)
  }
  return count
}

// Moves flagged for review start unchecked so they're only applied on purpose
function defaultSelection(moves: SmartOrganizeFileMove[]): Set<number> {
  return new Set(moves.filter((m) => !m.needs_review).map((m) => m.file_id))
}

function FolderNode({
  node,
  depth,
  selectedFiles,
  onFileToggle,
}: {
  node: FolderTreeNode
  depth: number
  selectedFiles: Set<number>
  onFileToggle: (fileId: number) => void
}) {
  const { t } = useAppTranslation(['files'])
  const [isExpanded, setIsExpanded] = useState(true)
  const totalFiles = countFilesInTree(node)

  return (
    <Box>
      <HStack
        px={3}
        py={2}
        pl={`${depth * 24 + 12}px`}
        cursor="pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        _hover={{ bg: 'teal.50' }}
        borderRadius="md"
        transition="all 0.15s"
      >
        <Icon as={isExpanded ? FiChevronDown : FiChevronRight} color="gray.500" boxSize="14px" />
        <Icon as={FiFolder} color="teal.500" boxSize="18px" />
        <Text fontSize="sm" fontWeight="semibold" color="gray.800" flex={1}>
          {node.name}
        </Text>
        <Badge colorScheme="teal" variant="subtle" borderRadius="full" fontSize="xs" px={2}>
          {totalFiles}
        </Badge>
        {node.description && (
          <Tooltip
            label={node.description}
            placement="top"
            hasArrow
            bg="gray.700"
            fontSize="sm"
            p={2}
            borderRadius="md"
          >
            <Box color="gray.400" cursor="help">
              <FiInfo size="14px" />
            </Box>
          </Tooltip>
        )}
      </HStack>

      <Collapse in={isExpanded} animateOpacity>
        {/* Child folders */}
        {node.children.map((child) => (
          <FolderNode
            key={child.path}
            node={child}
            depth={depth + 1}
            selectedFiles={selectedFiles}
            onFileToggle={onFileToggle}
          />
        ))}

        {/* Files in this folder */}
        {node.files.map((move) => {
          const isSelected = selectedFiles.has(move.file_id)
          const hasRename = !!move.suggested_name && move.suggested_name !== move.current_name

          return (
            <HStack
              key={move.file_id}
              px={3}
              py={2}
              pl={`${(depth + 1) * 24 + 12}px`}
              bg={isSelected ? 'teal.25' : 'transparent'}
              _hover={{ bg: isSelected ? 'teal.50' : 'gray.50' }}
              borderRadius="md"
              transition="all 0.15s"
              spacing={3}
            >
              <Checkbox
                isChecked={isSelected}
                onChange={() => onFileToggle(move.file_id)}
                colorScheme="teal"
                size="sm"
              />
              <Icon as={FiFile} color="gray.400" boxSize="14px" flexShrink={0} />
              <Box flex={1} minW={0}>
                <HStack spacing={2} flexWrap="wrap">
                  <Text
                    fontSize="sm"
                    color="gray.600"
                    fontFamily="mono"
                    noOfLines={1}
                    title={move.current_name}
                  >
                    {move.current_name}
                  </Text>
                  {hasRename && (
                    <>
                      <Icon as={FiArrowRight} color="teal.400" boxSize="12px" flexShrink={0} />
                      <Text
                        fontSize="sm"
                        color="teal.600"
                        fontWeight="medium"
                        fontFamily="mono"
                        noOfLines={1}
                        title={move.suggested_name}
                      >
                        {move.suggested_name}
                      </Text>
                    </>
                  )}
                </HStack>
              </Box>
              {hasRename && (
                <Badge
                  colorScheme="green"
                  variant="solid"
                  borderRadius="full"
                  fontSize="xs"
                  flexShrink={0}
                >
                  Rename
                </Badge>
              )}
              {move.needs_review && (
                <Tooltip
                  label={
                    move.jev_suggested_folder
                      ? t('smartOrganize.betterFit', { ns: 'files', folder: move.jev_suggested_folder })
                      : t('smartOrganize.needsReviewHint', { ns: 'files' })
                  }
                  placement="top"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  fontSize="sm"
                  p={3}
                  borderRadius="md"
                  maxW="300px"
                >
                  <Badge
                    colorScheme="orange"
                    variant="subtle"
                    borderRadius="full"
                    fontSize="xs"
                    flexShrink={0}
                    cursor="help"
                  >
                    {t('smartOrganize.needsReview', {
                      ns: 'files',
                      percent: Math.round((move.confidence ?? 0) * 100),
                    })}
                  </Badge>
                </Tooltip>
              )}
              {move.reasoning && (
                <Tooltip
                  label={move.reasoning}
                  placement="top"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  fontSize="sm"
                  p={3}
                  borderRadius="md"
                  maxW="300px"
                >
                  <Box cursor="help" color="blue.500" _hover={{ color: 'blue.600' }} flexShrink={0}>
                    <FiInfo size="14px" />
                  </Box>
                </Tooltip>
              )}
            </HStack>
          )
        })}
      </Collapse>
    </Box>
  )
}

export default function SmartOrganizeModal({
  isOpen,
  onClose,
  folder,
  selectedFileIds,
  skippedCount = 0,
  folderFileCount,
  onOrganizeComplete,
}: SmartOrganizeModalProps) {
  const [instructions, setInstructions] = useState('')
  const [plan, setPlan] = useState<SmartOrganizePlan | null>(null)
  const [selectedFiles, setSelectedFiles] = useState<Set<number>>(new Set())
  const [isGenerating, setIsGenerating] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeJob, setActiveJob] = useState<SmartOrganizeJob | null>(null)
  const [jobPollingInterval, setJobPollingInterval] = useState<NodeJS.Timeout | null>(null)
  const [lastJobId, setLastJobId] = useState<string | null>(null)
  const toast = useToast()
  const { t, currentLanguage } = useAppTranslation(['files', 'common'])

  // The API addresses the top of the tree as the literal 'root', so a missing folder is not
  // "no scope" here — it is the files that live outside every folder, and a plan applied
  // against it creates its folders at the top level.
  const scope: FolderScope = folder?.id ?? 'root'
  const scopeName = folder?.name ?? t('fileExplorer.allDocuments', { ns: 'files' })

  const isScoped = (selectedFileIds?.length ?? 0) > 0
  // A restored plan is folder-wide, so it can cover files outside the current selection.
  const hasStaleRestoredPlan =
    isScoped &&
    (plan?.file_moves?.length ?? 0) > 0 &&
    !plan!.file_moves.every((move) => selectedFileIds!.includes(move.file_id))

  const EXAMPLE_INSTRUCTIONS = [
    t('smartOrganize.exampleByClient', { ns: 'files' }),
    t('smartOrganize.exampleByType', { ns: 'files' }),
    t('smartOrganize.exampleByDate', { ns: 'files' }),
    t('smartOrganize.exampleByProject', { ns: 'files' }),
    t('smartOrganize.exampleByContent', { ns: 'files' }),
  ]

  const folderTree = useMemo(() => {
    if (!plan) return []
    return buildFolderTree(plan)
  }, [plan])

  const allFileIds = useMemo(() => {
    if (!plan) return new Set<number>()
    return new Set(plan.file_moves.map((m) => m.file_id))
  }, [plan])

  const reviewCount = useMemo(
    () => plan?.file_moves.filter((m) => m.needs_review).length ?? 0,
    [plan]
  )

  useEffect(() => {
    if (isOpen) {
      setInstructions('')
      setPlan(null)
      setSelectedFiles(new Set())
      setError(null)
      setActiveJob(null)
      setLastJobId(null)

      if (jobPollingInterval) {
        clearInterval(jobPollingInterval)
        setJobPollingInterval(null)
      }

      checkExistingJobs()
    } else {
      if (jobPollingInterval) {
        clearInterval(jobPollingInterval)
        setJobPollingInterval(null)
      }
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (jobPollingInterval) {
        clearInterval(jobPollingInterval)
      }
    }
  }, [jobPollingInterval])

  const checkExistingJobs = async () => {
    try {
      const response = await apiService.getFolderSmartOrganizeJobs(scope)
      const activeJobData = response.jobs.find(
        (job) => job.status === 'pending' || job.status === 'processing'
      )

      if (activeJobData) {
        setActiveJob({
          job_id: activeJobData.job_id,
          status: activeJobData.status,
          progress: activeJobData.progress,
          total_files: activeJobData.total_files,
          processed_files: activeJobData.processed_files,
          created_at: activeJobData.created_at,
          completed_at: activeJobData.completed_at,
        })
        setInstructions(activeJobData.instructions || '')
        startJobPolling(activeJobData.job_id)
      } else if (response.latest_completed?.results) {
        const results = response.latest_completed.results as SmartOrganizePlan
        setPlan(results)
        setLastJobId(response.latest_completed.id)
        setInstructions(response.latest_completed.instructions || '')
        setSelectedFiles(defaultSelection(results.file_moves))
      }
    } catch (error) {
      console.error('Failed to check existing jobs:', error)
    }
  }

  const startJobPolling = (jobId: string) => {
    const interval = setInterval(async () => {
      try {
        const status = await apiService.getSmartOrganizeJobStatus(jobId)
        setActiveJob((prev) => (prev ? { ...prev, ...status } : null))

        if (status.status === 'completed') {
          try {
            const results = await apiService.getSmartOrganizeJobResults(jobId)
            setPlan(results.plan)
            setLastJobId(jobId)
            setSelectedFiles(defaultSelection(results.plan.file_moves))
            setActiveJob(null)
            clearInterval(interval)
            setJobPollingInterval(null)

            toast({
              title: t('smartOrganize.jobCompleted', { ns: 'files' }),
              description: t('smartOrganize.jobCompletedDescription', {
                ns: 'files',
                count: results.plan.file_moves.length,
              }),
              status: 'success',
              duration: 5000,
            })
          } catch (error) {
            console.error('Failed to get job results:', error)
            setError('Failed to get organization plan')
            setActiveJob(null)
            clearInterval(interval)
            setJobPollingInterval(null)
          }
        } else if (status.status === 'failed') {
          setError(status.error_message || 'Job failed')
          setActiveJob(null)
          clearInterval(interval)
          setJobPollingInterval(null)
        } else if (status.status === 'cancelled') {
          setActiveJob(null)
          clearInterval(interval)
          setJobPollingInterval(null)
        }
      } catch (error) {
        console.error('Job polling error:', error)
        clearInterval(interval)
        setJobPollingInterval(null)
        setActiveJob(null)
      }
    }, 2000)

    setJobPollingInterval(interval)
  }

  const handleGeneratePlan = async () => {
    setIsGenerating(true)
    setError(null)
    setPlan(null)
    setSelectedFiles(new Set())

    try {
      const locale = currentLanguage === 'es' ? 'es' : 'en'
      const response = await apiService.generateSmartOrganize(
        scope,
        instructions.trim() || undefined,
        true,
        locale,
        isScoped ? selectedFileIds : undefined
      )

      if (response.async && response.job_id) {
        setActiveJob({
          job_id: response.job_id,
          status: response.status || 'pending',
          progress: 0,
          total_files: response.total_files || 0,
          processed_files: 0,
          created_at: new Date().toISOString(),
        })
        startJobPolling(response.job_id)

        toast({
          title: t('smartOrganize.jobStarted', { ns: 'files' }),
          description: t('smartOrganize.jobStartedDescription', {
            ns: 'files',
            count: response.total_files || 0,
          }),
          status: 'info',
          duration: 5000,
        })
      } else if (response.plan) {
        setPlan(response.plan)
        setSelectedFiles(defaultSelection(response.plan.file_moves))

        if (response.plan.file_moves.length === 0) {
          setError(t('smartOrganize.noSuggestions', { ns: 'files' }))
        }
      }
    } catch (err: any) {
      console.error(err)
      setError(
        err.response?.data?.error ||
          err.message ||
          t('smartOrganize.generateError', { ns: 'files' })
      )
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCancelJob = async () => {
    if (!activeJob) return

    try {
      await apiService.cancelSmartOrganizeJob(activeJob.job_id)
      setActiveJob(null)
      if (jobPollingInterval) {
        clearInterval(jobPollingInterval)
        setJobPollingInterval(null)
      }

      toast({
        title: t('smartOrganize.jobCancelled', { ns: 'files' }),
        status: 'info',
        duration: 3000,
      })
    } catch (error) {
      console.error('Failed to cancel job:', error)
      setError('Failed to cancel job')
    }
  }

  const handleFileToggle = (fileId: number) => {
    const newSelected = new Set(selectedFiles)
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId)
    } else {
      newSelected.add(fileId)
    }
    setSelectedFiles(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedFiles.size === allFileIds.size) {
      setSelectedFiles(new Set())
    } else {
      setSelectedFiles(new Set(allFileIds))
    }
  }

  const handleApply = async () => {
    if (!plan || selectedFiles.size === 0) {
      setError(t('smartOrganize.selectAtLeastOne', { ns: 'files' }))
      return
    }

    setIsApplying(true)
    setError(null)

    try {
      const selectedMoves = plan.file_moves
        .filter((m) => selectedFiles.has(m.file_id))
        .map((m) => ({
          file_id: m.file_id,
          target_folder_path: m.target_folder,
          new_name:
            m.suggested_name && m.suggested_name !== m.current_name ? m.suggested_name : undefined,
        }))

      const result = await apiService.applySmartOrganize({
        job_id: lastJobId || '',
        folder_id: folder?.id ?? null,
        selected_moves: selectedMoves,
      })

      toast({
        title: t('smartOrganize.organizeSuccess', { ns: 'files' }),
        description: t('smartOrganize.organizeSuccessDescription', {
          ns: 'files',
          moves: result.success,
          folders: result.folders_created,
        }),
        status: result.errors.length === 0 ? 'success' : 'warning',
        duration: 5000,
      })

      if (result.errors.length > 0) {
        console.error('Organize errors:', result.errors)
      }

      try {
        await apiService.clearFolderSmartOrganizeJobs(scope)
      } catch (clearError) {
        console.error('Failed to clear smart organize jobs:', clearError)
      }

      onOrganizeComplete()
      onClose()
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.message ||
          t('smartOrganize.organizeError', { ns: 'files' })
      )
    } finally {
      setIsApplying(false)
    }
  }

  const handleUseExample = (example: string) => {
    setInstructions(example)
  }

  const handleDiscardPlan = async () => {
    try {
      await apiService.clearFolderSmartOrganizeJobs(scope)
      setPlan(null)
      setSelectedFiles(new Set())
      setInstructions('')
      setError(null)
      setLastJobId(null)

      toast({
        title: t('smartOrganize.discardPlan', { ns: 'files' }),
        status: 'info',
        duration: 3000,
      })
    } catch (error) {
      console.error('Failed to clear smart organize jobs:', error)
      setError('Failed to discard plan')
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent maxW="90vw" maxH="90vh" borderRadius="lg" boxShadow="xl" overflow="hidden">
        <ModalHeader
          bgGradient="linear(to-r, teal.500, cyan.600)"
          color="white"
          py={6}
          borderRadius="lg lg 0 0"
        >
          <HStack spacing={3}>
            <FiGrid size="24px" />
            <Text fontSize="xl" fontWeight="bold">
              {t('smartOrganize.title', { ns: 'files' })}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="white" size="lg" top={4} right={4} />

        <ModalBody overflow="auto" p={6} bg="gray.50">
          <VStack spacing={6} align="stretch">
            {/* Folder Info */}
            <Box p={4} bg="teal.25" borderRadius="lg" border="1px solid" borderColor="teal.100">
              <HStack spacing={3}>
                <Box p={2} bg="teal.100" borderRadius="md" color="teal.600">
                  <FiFolder size="20px" />
                </Box>
                <VStack align="start" spacing={1}>
                  <Text fontSize="md" fontWeight="semibold" color="teal.800">
                    {scopeName}
                  </Text>
                  <Text fontSize="sm" color="teal.600">
                    {isScoped
                      ? t('smartOrganize.scopeSelected', {
                          ns: 'files',
                          count: selectedFileIds!.length,
                        })
                      : folderFileCount != null
                        ? t(
                            folder
                              ? 'smartOrganize.scopeAllInFolder'
                              : 'smartOrganize.scopeAllAtRoot',
                            { ns: 'files', count: folderFileCount }
                          )
                        : t('smartOrganize.description', { ns: 'files' })}
                  </Text>
                </VStack>
              </HStack>
            </Box>

            {hasStaleRestoredPlan && (
              <Alert status="info" borderRadius="lg">
                <AlertIcon />
                <Text fontSize="sm">{t('smartOrganize.scopeStaleResults', { ns: 'files' })}</Text>
              </Alert>
            )}

            {skippedCount > 0 && (
              <Alert status="info" borderRadius="lg">
                <AlertIcon />
                <Text fontSize="sm">
                  {t('smartOrganize.someSkipped', { ns: 'files', count: skippedCount })}
                </Text>
              </Alert>
            )}

            {/* Instructions Input */}
            <Box
              p={5}
              bg="white"
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="sm"
            >
              <VStack spacing={4} align="stretch">
                <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                  {t('smartOrganize.instructionsTitle', { ns: 'files' })}
                </Text>
                <Textarea
                  placeholder={t('smartOrganize.instructionsPlaceholder', { ns: 'files' })}
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  rows={3}
                  isDisabled={isGenerating}
                  borderRadius="lg"
                  border="2px solid"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: 'teal.400',
                    boxShadow: '0 0 0 1px rgba(56, 178, 172, 0.3)',
                  }}
                  _hover={{ borderColor: 'gray.300' }}
                  fontSize="md"
                  resize="vertical"
                />

                {/* Example Instructions */}
                <Box p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                  <Text fontSize="sm" fontWeight="medium" mb={3} color="gray.700">
                    {t('smartOrganize.exampleInstructionsHeader', { ns: 'files' })}
                  </Text>
                  <VStack spacing={2} align="stretch">
                    {EXAMPLE_INSTRUCTIONS.map((example, index) => (
                      <Box
                        key={index}
                        p={2}
                        bg="white"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.200"
                        cursor="pointer"
                        _hover={{
                          borderColor: 'teal.300',
                          bg: 'teal.25',
                          transform: 'translateY(-1px)',
                        }}
                        transition="all 0.2s ease"
                        onClick={() => handleUseExample(example)}
                      >
                        <Text fontSize="sm" color="teal.600" fontWeight="medium">
                          {example}
                        </Text>
                      </Box>
                    ))}
                  </VStack>
                </Box>

                <Button
                  colorScheme="teal"
                  onClick={handleGeneratePlan}
                  isLoading={isGenerating}
                  loadingText={t('smartOrganize.analyzing', { ns: 'files' })}
                  leftIcon={<FiGrid />}
                  isDisabled={!!activeJob}
                  borderRadius="lg"
                  py={3}
                  h="auto"
                  fontWeight="semibold"
                  fontSize="md"
                  _hover={{
                    transform: 'translateY(-1px)',
                    boxShadow: 'lg',
                  }}
                  transition="all 0.2s"
                >
                  {activeJob
                    ? t('smartOrganize.jobInProgress', { ns: 'files' })
                    : t('smartOrganize.generatePlan', { ns: 'files' })}
                </Button>
              </VStack>
            </Box>

            {/* Error Display */}
            {error && (
              <Alert status="error">
                <AlertIcon />
                <Text>{error}</Text>
              </Alert>
            )}

            {/* Job Progress Display */}
            {activeJob && (
              <Box
                p={5}
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="blue.200"
                boxShadow="sm"
              >
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between">
                    <HStack spacing={3}>
                      <Box p={2} bg="blue.100" borderRadius="md" color="blue.600">
                        <FiClock size="20px" />
                      </Box>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold" fontSize="lg" color="blue.800">
                          {activeJob.status === 'pending' &&
                            t('smartOrganize.jobPending', { ns: 'files' })}
                          {activeJob.status === 'processing' &&
                            t('smartOrganize.jobProcessing', { ns: 'files' })}
                        </Text>
                        <Text fontSize="sm" color="blue.600">
                          {t('smartOrganize.processingFiles', {
                            ns: 'files',
                            processed: activeJob.processed_files,
                            total: activeJob.total_files,
                          })}
                        </Text>
                      </VStack>
                    </HStack>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="red"
                      onClick={handleCancelJob}
                      leftIcon={<FiX />}
                    >
                      {t('buttons.cancel', { ns: 'common' })}
                    </Button>
                  </HStack>

                  <Box>
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="sm" fontWeight="medium" color="gray.700">
                        {t('smartOrganize.progress', { ns: 'files' })}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {activeJob.progress}%
                      </Text>
                    </HStack>
                    <Progress
                      value={activeJob.progress}
                      colorScheme="blue"
                      borderRadius="full"
                      size="lg"
                      hasStripe
                      isAnimated
                    />
                  </Box>

                  <Text fontSize="xs" color="gray.500" textAlign="center">
                    {t('smartOrganize.jobStartedAt', {
                      ns: 'files',
                      time: new Date(activeJob.created_at).toLocaleTimeString(),
                    })}
                  </Text>
                </VStack>
              </Box>
            )}

            {/* Organization Preview - Tree View */}
            {plan && (
              <Box
                p={5}
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="sm"
              >
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between">
                    <HStack spacing={3}>
                      <FiFolder size="20px" color="teal" />
                      <Text fontWeight="bold" fontSize="lg" color="gray.800">
                        {t('smartOrganize.organizationPreview', { ns: 'files' })}
                      </Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Badge colorScheme="teal" variant="solid" borderRadius="full" px={3} py={1}>
                        {t('smartOrganize.proposedStructure', {
                          ns: 'files',
                          folders: plan.folders.length,
                          moves: plan.file_moves.length,
                        })}
                      </Badge>
                    </HStack>
                  </HStack>

                  {/* Summary */}
                  {plan.summary && (
                    <Box
                      p={3}
                      bg="teal.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="teal.100"
                    >
                      <HStack spacing={2}>
                        <Text fontSize="sm" fontWeight="medium" color="teal.700">
                          {t('smartOrganize.summary', { ns: 'files' })}:
                        </Text>
                        <Text fontSize="sm" color="teal.800">
                          {plan.summary}
                        </Text>
                      </HStack>
                    </Box>
                  )}

                  {/* Select All Controls */}
                  <HStack justify="space-between">
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">
                        {t('smartOrganize.selected', { ns: 'files', count: selectedFiles.size })}
                      </Text>
                      {reviewCount > 0 && (
                        <Text fontSize="xs" color="orange.600">
                          {t('smartOrganize.reviewCount', { ns: 'files', count: reviewCount })}
                        </Text>
                      )}
                    </VStack>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleSelectAll}
                      borderRadius="lg"
                      colorScheme="teal"
                      _hover={{ transform: 'translateY(-1px)' }}
                      transition="all 0.2s"
                    >
                      {selectedFiles.size === allFileIds.size
                        ? t('buttons.deselectAll', { ns: 'common' })
                        : t('buttons.selectAll', { ns: 'common' })}
                    </Button>
                  </HStack>

                  {/* Tree View */}
                  <Box
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.200"
                    overflow="hidden"
                    bg="gray.25"
                    py={2}
                  >
                    {folderTree.map((node) => (
                      <FolderNode
                        key={node.path}
                        node={node}
                        depth={0}
                        selectedFiles={selectedFiles}
                        onFileToggle={handleFileToggle}
                      />
                    ))}
                  </Box>
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter bg="white" borderTop="1px solid" borderColor="gray.200" py={4}>
          <HStack spacing={3} w="full" justify="space-between">
            <Text fontSize="sm" color="gray.600">
              {plan
                ? t('smartOrganize.filesSelectedCount', {
                    ns: 'files',
                    selected: selectedFiles.size,
                    total: plan.file_moves.length,
                  })
                : t('smartOrganize.generateToPreview', { ns: 'files' })}
            </Text>
            <HStack spacing={3}>
              <Button
                variant="ghost"
                onClick={onClose}
                isDisabled={isApplying}
                borderRadius="lg"
                px={6}
                _hover={{ bg: 'gray.100' }}
              >
                {t('buttons.cancel', { ns: 'common' })}
              </Button>
              {plan && (
                <Button
                  variant="outline"
                  colorScheme="red"
                  onClick={handleDiscardPlan}
                  isDisabled={isApplying || !!activeJob}
                  borderRadius="lg"
                  px={6}
                  leftIcon={<FiX />}
                  _hover={{ bg: 'red.50' }}
                >
                  {t('smartOrganize.discardPlan', { ns: 'files' })}
                </Button>
              )}
              {plan && (
                <Button
                  colorScheme="teal"
                  onClick={handleApply}
                  isLoading={isApplying}
                  loadingText={t('smartOrganize.applyingChanges', { ns: 'files' })}
                  leftIcon={<FiCheck />}
                  isDisabled={selectedFiles.size === 0}
                  borderRadius="lg"
                  px={6}
                  py={3}
                  h="auto"
                  fontWeight="semibold"
                  _hover={{
                    transform: 'translateY(-1px)',
                    boxShadow: 'lg',
                  }}
                  transition="all 0.2s"
                >
                  {t('smartOrganize.applyChanges', { ns: 'files', count: selectedFiles.size })}
                </Button>
              )}
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
